import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, Upload, message } from 'antd';
import { DeleteOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { deleteContractDocument, getContractDocumentFile, getContractDocuments, postContractDocument } from '../store/user/thunks';

type AnyRecord = Record<string, any>;

interface Props {
    organizationId: string;
    contractId: string;
    getContractDocuments?: any;
    postContractDocument?: any;
    deleteContractDocument?: any;
    getContractDocumentFile?: any;
}

const pick = (source: AnyRecord | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) if (source[key] !== undefined && source[key] !== null) return source[key];
    return undefined;
};

const dateText = (value?: string) => value ? new Date(value).toLocaleDateString('es-EC') : '—';

const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('No se pudo leer el PDF.'));
    reader.readAsDataURL(file);
});

const ContractDocuments: React.FC<Props> = props => {
    const { organizationId, contractId, getContractDocuments, postContractDocument, deleteContractDocument, getContractDocumentFile } = props;
    const [items, setItems] = useState<AnyRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getContractDocuments?.(organizationId, contractId);
            setItems(Array.isArray(response) ? response : []);
        } finally { setLoading(false); }
    }, [contractId, getContractDocuments, organizationId]);

    useEffect(() => { load(); }, [load]);

    const close = () => { setOpen(false); setFile(null); form.resetFields(); };

    const submit = async () => {
        const values = await form.validateFields();
        if (!file) { message.error('Seleccione el PDF del contrato o adenda.'); return; }
        if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) { message.error('Solo se permite PDF.'); return; }
        setSaving(true);
        try {
            const response = await postContractDocument?.(organizationId, contractId, {
                documentType: values.documentType,
                versionNumber: values.versionNumber || null,
                notes: values.notes || null,
                fileBase64: await fileToBase64(file),
                fileName: file.name,
                contentType: 'application/pdf'
            });
            if (Array.isArray(response)) setItems(response);
            close();
        } finally { setSaving(false); }
    };

    const openFile = async (row: AnyRecord) => {
        const response = await getContractDocumentFile?.(organizationId, contractId, String(pick(row, 'id', 'Id')));
        const url = pick(response, 'uri', 'Uri', 'url', 'Url', 'blobUri', 'BlobUri');
        if (url) window.open(String(url), '_blank', 'noopener,noreferrer');
        else message.warning('No se recibió un enlace temporal.');
    };

    const inactivate = (row: AnyRecord) => Modal.confirm({
        title: 'Anular documento',
        content: 'El archivo se conservará en Azure y el registro quedará ANULADO para auditoría. ¿Desea continuar?',
        okText: 'Anular', cancelText: 'Cancelar', okButtonProps: { danger: true },
        onOk: async () => {
            const response = await deleteContractDocument?.(organizationId, contractId, String(pick(row, 'id', 'Id')));
            if (Array.isArray(response)) setItems(response);
        }
    });

    return <Card
        title="Contrato firmado y adendas"
        extra={<Space><Button onClick={load}>Actualizar</Button><Button type="primary" icon={<UploadOutlined />} onClick={() => setOpen(true)}>Cargar documento</Button></Space>}
    >
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Evidencia comercial opcional" description="Los términos y condiciones legales siguen gestionándose por el flujo legal existente. Aquí se conserva el contrato firmado y sus adendas, con versiones y trazabilidad." />
        <Table
            rowKey={row => String(pick(row, 'id', 'Id'))}
            loading={loading}
            dataSource={items}
            pagination={{ pageSize: 6 }}
            locale={{ emptyText: 'No hay contratos o adendas cargados.' }}
            columns={[
                { title: 'Tipo', dataIndex: 'documentType' },
                { title: 'Versión', dataIndex: 'versionNumber' },
                { title: 'Archivo', dataIndex: 'fileName' },
                { title: 'Fecha', render: (_: any, row: AnyRecord) => dateText(pick(row, 'createdAt', 'CreatedAt')) },
                { title: 'Estado', render: (_: any, row: AnyRecord) => <Tag color={pick(row, 'status', 'Status') === 'ANULADO' ? 'red' : 'green'}>{pick(row, 'status', 'Status') || 'ACTIVO'}</Tag> },
                { title: 'Acciones', render: (_: any, row: AnyRecord) => <Space><Button size="small" icon={<EyeOutlined />} onClick={() => openFile(row)}>Ver PDF</Button>{String(pick(row, 'status', 'Status')) !== 'ANULADO' && <Button danger size="small" icon={<DeleteOutlined />} onClick={() => inactivate(row)}>Anular</Button>}</Space> }
            ]}
        />
        <Modal open={open} title="Cargar contrato o adenda" okText="Guardar" cancelText="Cancelar" onCancel={close} onOk={submit} confirmLoading={saving} destroyOnClose>
            <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="Solo PDF, máximo 15 MB" description="Cargar una nueva versión no reemplaza las versiones anteriores. La anulación es lógica y conserva el archivo." />
            <Form form={form} layout="vertical">
                <Form.Item label="Tipo de documento" name="documentType" initialValue="CONTRATO" rules={[{ required: true }]}>
                    <Select options={[{ value: 'CONTRATO', label: 'Contrato firmado' }, { value: 'ADENDA', label: 'Adenda' }]} />
                </Form.Item>
                <Form.Item label="Versión (opcional)" name="versionNumber" tooltip="Si se deja vacío, se asigna la siguiente versión disponible."><InputNumber min={1} precision={0} style={{ width: '100%' }} /></Form.Item>
                <Form.Item label="Notas" name="notes"><Input.TextArea rows={2} maxLength={1000} /></Form.Item>
                <Upload accept="application/pdf,.pdf" maxCount={1} beforeUpload={selected => { setFile(selected); return false; }} onRemove={() => { setFile(null); return true; }} fileList={file ? [file as any] : []}><Button icon={<UploadOutlined />}>Seleccionar PDF</Button></Upload>
            </Form>
        </Modal>
    </Card>;
};

export default connect(null, { getContractDocuments, postContractDocument, deleteContractDocument, getContractDocumentFile })(ContractDocuments);
